import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../../config';

const CollectorModal = ({ show, onHide, onCollectorSaved, editCollector }) => {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    assignedLabs: [],
    isActive: true
  });
  const [labs, setLabs] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      fetchLabs();
      if (editCollector) {
        setFormData({
          name: editCollector.name || '',
          mobile: editCollector.mobile || '',
          assignedLabs: editCollector.assignedLabs?.map(lab => lab._id) || [],
          isActive: editCollector.isActive !== undefined ? editCollector.isActive : true
        });
      } else {
        resetForm();
      }
    }
  }, [show, editCollector]);

  const fetchLabs = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/labs?isActive=true`);
      setLabs(response.data);
    } catch (error) {
      console.error('Error fetching labs:', error);
      toast.error('Failed to load labs');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      mobile: '',
      assignedLabs: [],
      isActive: true
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const dataToSend = {
        ...formData,
        mobile: formData.mobile.trim()
      };

      let response;
      if (editCollector) {
        response = await axios.put(`${config.API_BASE_URL}/collectors/${editCollector._id}`, dataToSend);
      } else {
        response = await axios.post(`${config.API_BASE_URL}/collectors`, dataToSend);
      }

      if (response.data.success) {
        toast.success(response.data.message);
        onCollectorSaved();
        onHide();
      }
    } catch (error) {
      console.error('Error saving collector:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save collector';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleLabChange = (labId, checked) => {
    setFormData(prev => ({
      ...prev,
      assignedLabs: checked
        ? [...prev.assignedLabs, labId]
        : prev.assignedLabs.filter(id => id !== labId)
    }));
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {editCollector ? 'Edit Collector' : 'Add New Collector'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Collector Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter collector name"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Mobile Number *</Form.Label>
                <Form.Control
                  type="tel"
                  value={formData.mobile}
                  onChange={(e) => setFormData(prev => ({ ...prev, mobile: e.target.value }))}
                  placeholder="Enter mobile number"
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-2">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Assigned Labs</Form.Label>
                <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {labs.length === 0 ? (
                    <p className="text-muted mb-0">No labs available</p>
                  ) : (
                    labs.map(lab => (
                      <Form.Check
                        key={lab._id}
                        type="checkbox"
                        id={`lab-${lab._id}`}
                        label={`${lab.name} - ${lab.address.substring(0, 50)}${lab.address.length > 50 ? '...' : ''}`}
                        checked={formData.assignedLabs.includes(lab._id)}
                        onChange={(e) => handleLabChange(lab._id, e.target.checked)}
                      />
                    ))
                  )}
                </div>
                <Form.Text className="text-muted">
                  Select labs this collector is assigned to
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {editCollector && (
            <Row className="g-3 mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    label="Active"
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                </Form.Group>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving || !formData.name.trim() || !formData.mobile.trim()}
          >
            {saving ? 'Saving...' : (editCollector ? 'Update Collector' : 'Add Collector')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CollectorModal;
